import {
  PanResponder,
  Platform,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

interface ActivityMonitorProps {
  maxActivityAgeMs?: number
  checkIntervalMs?: number
  enabled?: boolean
  children: React.ReactNode
  style?: StyleProp<ViewStyle>
  setActive: (active: boolean) => void
}

const events = [
  'mousemove',
  'mousedown',
  'touchstart',
  'touchmove',
  'keydown',
  'click',
  'scroll',
] as const

const WebActivityMonitor = ({
  children,
  enabled,
  handleActivity,
}: {
  children: React.ReactNode
  enabled?: boolean
  handleActivity: (e?: any) => void
}) => {
  useEffect(() => {
    if (!enabled) return
    events.map((event) => document.addEventListener(event, handleActivity))

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, handleActivity)
      )
    }
  }, [enabled, handleActivity])

  return children
}
const ActivityMonitor = ({
  maxActivityAgeMs = 2000,
  checkIntervalMs = 1000,
  enabled = true,
  children,
  setActive,
  style,
}: ActivityMonitorProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const lastActivity = useRef<number | undefined>(undefined)

  const isInactive = useRef<boolean>(false)

  const handleStop = useCallback(() => {
    if (intervalRef.current) clearTimeout(intervalRef.current)
    intervalRef.current = null
  }, [])

  const checkActivity = useCallback(() => {
    if (!lastActivity.current) return
    const now = Date.now()
    const diff = now - lastActivity.current

    if (diff > maxActivityAgeMs) {
      if (!isInactive.current) {
        isInactive.current = true
        setActive(false)
      }
    }
  }, [maxActivityAgeMs, setActive])

  const handleActivity = useCallback((e?: any) => {
    // Store the  activity time
    lastActivity.current = Date.now()
    if (isInactive.current) {
      isInactive.current = false
      setActive(true)
    }
    return false
  }, [])

  useEffect(() => {
    if (!enabled) return
    handleActivity()
    intervalRef.current = setInterval(checkActivity, checkIntervalMs)

    return () => {
      handleStop()
    }
  }, [checkActivity, checkIntervalMs, enabled, handleStop, handleActivity])

  const panResponder = useMemo(
    () =>
      // This is weird that the lint is complaining about this
      // we are not using the ref directly, we are using the handleActivity event handler instead
      // eslint-disable-next-line react-hooks/refs
      PanResponder.create({
        onStartShouldSetPanResponderCapture: handleActivity,
        onMoveShouldSetPanResponderCapture: handleActivity,
        onPanResponderTerminationRequest: handleActivity,
      }),
    [handleActivity]
  )

  if (Platform.OS === 'web') {
    return (
      <WebActivityMonitor enabled={enabled} handleActivity={handleActivity}>
        {children}
      </WebActivityMonitor>
    )
  }

  return (
    <View
      style={style}
      className="flex-1"
      collapsable={false}
      {...(enabled ? panResponder.panHandlers : null)}
    >
      {children}
    </View>
  )
}

export default ActivityMonitor
